import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { JtConnectionBanner } from './JtConnectionBanner';
import { useAuthStore } from '../../stores/auth.store';
import { endManageAs } from '../../api/manage-as.api';
import { noAccessPath } from '../../api/client';

export function AppLayout() {
  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      minWidth: 'var(--layout-min-width)',
      backgroundColor: 'var(--color-surface-page)',
      overflow: 'hidden',
    }}>
      <Sidebar />

      {/* Content area: topbar + scrollable page */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        <TakeoverBanner />
        <JtConnectionBanner />
        <Topbar />
        <main style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-6)' }}>
          <Outlet />
        </main>
      </div>

      {/* Linki · Ask side tab removed in the dummy-data cleanup pass. The
       *  backend Linki service only exposes /draft and /rewrite (single-shot
       *  email text generation) — there is no chat / multi-turn / narrative
       *  Q&A endpoint to wire this tab to. Restore the markup below when a
       *  conversational Linki backend lands.
       *
       *  Previous markup is preserved in git history; the visual styling
       *  uses --linki-tab-* CSS vars which remain defined for future revival.
       */}
    </div>
  );
}

/**
 * Visible indicator that the session is impersonating another account —
 * Section 13B.2 super-admin takeover, or an org admin's "Manage as" session.
 *
 * "End session" / "Exit takeover" revokes the token server-side (best effort:
 * legacy takeover tokens without a jti can't be revoked and simply expire),
 * then closes the tab. If the browser won't let script close the tab, the
 * user lands on a "session ended" page instead.
 */
function TakeoverBanner() {
  const isTakeover = useAuthStore((s) => s.isTakeover);
  const kind = useAuthStore((s) => s.impersonationKind);
  const user = useAuthStore((s) => s.user);
  const takeoverBy = useAuthStore((s) => s.takeoverBy);
  const expiresAt = useAuthStore((s) => s.impersonationExpiresAt);
  const isManageAs = kind === 'manage_as';
  const minutesLeft = useMinutesLeft(isManageAs ? expiresAt : null);
  const [ending, setEnding] = useState(false);

  if (!isTakeover || !user) return null;

  const endSession = async () => {
    if (ending) return;
    setEnding(true);
    try {
      await endManageAs();
    } catch {
      // Already ended/expired, or a legacy takeover token without a jti —
      // closing the tab is still the right outcome.
    }
    // Drop the persisted session straight away. In-memory state goes with the
    // tab, or NoAccessPage clears it if the tab can't be closed. (clearAuth()
    // here would first bounce through AuthGuard's login redirect.)
    useAuthStore.persist.clearStorage();
    window.close();
    // window.close() is a no-op for tabs the browser doesn't consider
    // script-opened. If we're still running shortly after, show the page.
    window.setTimeout(() => {
      window.location.replace(noAccessPath(isManageAs ? 'MANAGE_AS_ENDED' : 'TAKEOVER_ENDED'));
    }, 150);
  };

  return (
    <div
      role="alert"
      style={{
        backgroundColor: 'var(--color-warning)',
        color: 'var(--color-text-inverse)',
        padding: 'var(--space-2) var(--space-5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-semibold)',
      }}
    >
      {isManageAs ? (
        <span>
          Managing Engage as <strong>{user.name || user.email}</strong>. Actions are recorded as {takeoverBy ?? 'you'}.
          {minutesLeft !== null && ` Ends in ${minutesLeft}m`}
        </span>
      ) : (
        <span>
          Acting as <strong>{user.name || user.email}</strong> at org <code>{user.org_id}</code>
          {takeoverBy ? ` (by ${takeoverBy})` : ''}
        </span>
      )}
      <button
        type="button"
        onClick={endSession}
        disabled={ending}
        style={{
          background: 'transparent',
          border: '1px solid var(--color-text-inverse)',
          color: 'var(--color-text-inverse)',
          padding: 'var(--space-1) var(--space-3)',
          borderRadius: 'var(--btn-radius)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          cursor: ending ? 'wait' : 'pointer',
          opacity: ending ? 0.7 : 1,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {ending ? 'Ending…' : isManageAs ? 'End session' : 'Exit takeover'}
      </button>
    </div>
  );
}

/** Whole minutes until `expiresAt`, refreshed every 30s. null when unknown. */
function useMinutesLeft(expiresAt: string | null): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);

  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now;
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / 60_000));
}
